// src/pages/Portfolio.jsx
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import GalleryGrid from "../components/portfolio/GalleryGrid";
import Lightbox from "../components/portfolio/Lightbox";
import { getWhatsAppLink } from "../components/whatsapp/getWhatsAppLink";
import { portfolioMedia } from "../data/portfolioMedia";

function Portfolio() {
  const location = useLocation();

  const cenografiaImages = portfolioMedia.cenografia.map((i) => i.src);

  const iluminacaoImages = portfolioMedia.iluminacao.map((i) => i.src);

  // VÍDEOS (PORTFÓLIO)
  const videoCenografiaSrc = "/videos/cenografia/01.mp4";
  const videoCenografiaPoster = "/images/portfolio/posters/cenografia-video.jpeg";

  const videoIluminacaoSrc = "/videos/iluminacao/01.mp4";
  const videoIluminacaoPoster = "/images/portfolio/posters/iluminacao-video.jpeg";

  // ===== SEO =====
  const title = "Portfólio de Cenografia e Iluminação Cênica | Cenovix";
  const description =
    "Portfólio de cenografia e iluminação cênica da Cenovix. Projetos com foco em impacto visual, atmosfera e resultado em foto e vídeo.";
  const url = "https://www.cenovix.com.br/portfolio";
  const ogImage = "https://www.cenovix.com.br/images/home/hero.jpg";

  const cenografiaItems = useMemo(
    () =>
      cenografiaImages.map((src, idx) => ({
        src,
        alt: `Cenografia Cenovix — projeto ${idx + 1}`,
      })),
    [cenografiaImages]
  );

  const iluminacaoItems = useMemo(
    () =>
      iluminacaoImages.map((src, idx) => ({
        src,
        alt: `Iluminação cênica Cenovix — projeto ${idx + 1}`,
      })),
    [iluminacaoImages]
  );

  const videoCenografiaItems = useMemo(
    () => [
      {
        src: videoCenografiaSrc,
        poster: videoCenografiaPoster,
        type: "video",
        alt: "Vídeo de cenografia Cenovix",
      },
    ],
    [videoCenografiaSrc, videoCenografiaPoster]
  );

  const videoIluminacaoItems = useMemo(
    () => [
      {
        src: videoIluminacaoSrc,
        poster: videoIluminacaoPoster,
        type: "video",
        alt: "Vídeo de iluminação cênica Cenovix",
      },
    ],
    [videoIluminacaoSrc, videoIluminacaoPoster]
  );

  const [lb, setLb] = useState({ open: false, index: 0, group: "cenografia" });

  const items = useMemo(() => {
    if (lb.group === "video-cenografia") return videoCenografiaItems;
    if (lb.group === "video-iluminacao") return videoIluminacaoItems;
    return lb.group === "iluminacao" ? iluminacaoItems : cenografiaItems;
  }, [
    lb.group,
    iluminacaoItems,
    cenografiaItems,
    videoCenografiaItems,
    videoIluminacaoItems,
  ]);

  const openLb = (group, index) => setLb({ open: true, group, index });
  const closeLb = () => setLb((s) => ({ ...s, open: false }));

  const prev = () =>
    setLb((s) => {
      const len = items.length || 1;
      return { ...s, index: (s.index - 1 + len) % len };
    });

  const next = () =>
    setLb((s) => {
      const len = items.length || 1;
      return { ...s, index: (s.index + 1) % len };
    });

  // ✅ WhatsApp do Portfólio (personalizado)
  const waLink = getWhatsAppLink("/portfolio");

  // ✅ Scroll para âncoras (/portfolio#cenografia | /portfolio#iluminacao)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");

    window.requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.hash]);

  // JSON-LD
  const portfolioJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description,
    url,
    isPartOf: {
      "@type": "WebSite",
      name: "Cenovix",
      url: "https://www.cenovix.com.br/",
    },
    about: [
      { "@type": "Thing", name: "Cenografia para eventos" },
      { "@type": "Thing", name: "Iluminação cênica para eventos" },
    ],
    mainEntity: {
      "@type": "ItemList",
      itemListElement: [
        ...cenografiaItems.map((it, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          item: {
            "@type": "CreativeWork",
            name: `Cenografia — projeto ${idx + 1}`,
            image: `https://www.cenovix.com.br${it.src}`,
          },
        })),
        ...iluminacaoItems.map((it, idx) => ({
          "@type": "ListItem",
          position: cenografiaItems.length + idx + 1,
          item: {
            "@type": "CreativeWork",
            name: `Iluminação cênica — projeto ${idx + 1}`,
            image: `https://www.cenovix.com.br${it.src}`,
          },
        })),
      ],
    },
  };

  return (
    <div className="portfolio">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index,follow,max-image-preview:large" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cenovix" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={ogImage} />
        <meta
          property="og:image:alt"
          content="Portfólio Cenovix: cenografia e iluminação cênica"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />

        <meta name="theme-color" content="#0b0b0c" />

        <script type="application/ld+json">
          {JSON.stringify(portfolioJsonLd)}
        </script>
      </Helmet>

      <section aria-label="Portfólio Cenovix">
        <h1>Portfólio de Cenografia e Iluminação Cênica</h1>
        <p>
          Projetos desenvolvidos a partir do espaço, da atmosfera e da experiência
          desejada — com foco em impacto visual e resultado em foto e vídeo.
        </p>
      </section>

      <section aria-label="Soluções">
        <div className="solution-cards">
          <div className="solution-card">
            <h3>Cenografia</h3>

            <div className="video-card">
              <video
                className="video-card__media"
                src={videoCenografiaSrc}
                poster={videoCenografiaPoster}
                controls
                playsInline
                preload="metadata"
                onClick={() => openLb("video-cenografia", 0)}
                onPlay={(e) => {
                  e.currentTarget.pause();
                  openLb("video-cenografia", 0);
                }}
              />
            </div>
          </div>

          <div className="solution-card">
            <h3>Iluminação</h3>

            <div className="video-card">
              <video
                className="video-card__media"
                src={videoIluminacaoSrc}
                poster={videoIluminacaoPoster}
                controls
                playsInline
                preload="metadata"
                onClick={() => openLb("video-iluminacao", 0)}
                onPlay={(e) => {
                  e.currentTarget.pause();
                  openLb("video-iluminacao", 0);
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <section
        id="cenografia"
        aria-label="Portfólio de cenografia"
        style={{ scrollMarginTop: 110 }}
      >
        <h2>Cenografia</h2>
        <GalleryGrid
          items={cenografiaItems}
          variant="editorial"
          onItemClick={(index) => openLb("cenografia", index)}
        />
      </section>

      <section
        id="iluminacao"
        aria-label="Portfólio de iluminação cênica"
        style={{ scrollMarginTop: 110 }}
      >
        <h2>Iluminação</h2>
        <GalleryGrid
          items={iluminacaoItems}
          variant="editorial"
          onItemClick={(index) => openLb("iluminacao", index)}
        />
      </section>

      <section aria-label="Contato para orçamento">
        <h2>Quer algo nesse nível para o seu evento?</h2>
        <p>
          Fale com a gente no WhatsApp e conte a <strong>data</strong>, o{" "}
          <strong>local</strong> e o <strong>tipo de evento</strong>.
        </p>

        <a
          className="cta"
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Pedir orçamento no WhatsApp"
        >
          Pedir orçamento no WhatsApp
        </a>
      </section>

      <Lightbox
        open={lb.open}
        items={items}
        index={lb.index}
        onClose={closeLb}
        onPrev={prev}
        onNext={next}
      />
    </div>
  );
}

export default Portfolio;
